import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../api/chat';
import { chatSocket } from '../api/ws';
import type { Conversation, Message } from '../types/message';
import type { User } from '../types/user';
import type { FriendRequest } from '../types/people';
import type { RootState } from './store';

export type View = 'conversations' | 'contacts' | 'groups';

// 右侧详情区内容(随视图与选中项变化;功能面板不再用弹窗)
export type RightContent =
  | { type: 'chat' } // 会话视图:聊天内容
  | { type: 'contact-card'; userId: number } // 通讯录:个人名片
  | { type: 'group-detail'; groupId: number } // 群聊视图:群聊详情(人数/成员)
  | { type: 'panel'; panel: 'add-friend' | 'requests' | 'blocked' | 'profile' | 'new-group' } // 功能面板
  | { type: 'none' };

export interface ChatState {
  conversations: Conversation[];
  contacts: User[];
  blocked: User[];
  pendingRequests: FriendRequest[];
  activeConversationId: number | null;
  messages: Record<number, Message[]>;
  loaded: Record<number, boolean>;
  hasMore: Record<number, boolean>;
  view: View;
  rightContent: RightContent;
  wsConnected: boolean;
  initialized: boolean;
}

const initialState: ChatState = {
  conversations: [],
  contacts: [],
  blocked: [],
  pendingRequests: [],
  activeConversationId: null,
  messages: {},
  loaded: {},
  hasMore: {},
  view: 'conversations',
  rightContent: { type: 'chat' },
  wsConnected: false,
  initialized: false,
};

let wsSubscribed = false;

const sortConversations = (list: Conversation[]) => {
  return [...list].sort((a, b) => {
    const ta = a.last_message?.id ?? -a.id;
    const tb = b.last_message?.id ?? -b.id;
    return tb - ta;
  });
};

// 消息按 id 升序排列;乐观更新(负 id 临时消息)始终排在最末
const sortMessages = (list: Message[]) =>
  [...list].sort((a, b) => {
    const ka = a.id < 0 ? Number.MAX_SAFE_INTEGER : a.id;
    const kb = b.id < 0 ? Number.MAX_SAFE_INTEGER : b.id;
    return ka - kb;
  });

// ---------- thunks ----------

export const initChat = createAsyncThunk('chat/init', async () => {
  const [conversations, contacts, blocked, pendingRequests] = await Promise.all([
    api.conversations(),
    api.contacts(),
    api.blockedUsers(),
    api.friendRequests(),
  ]);
  return { conversations, contacts, blocked, pendingRequests };
});

// 建立 WebSocket 订阅(处理器只注册一次,连接可反复建立)
export const connectChatSocket = createAsyncThunk('chat/connectSocket', async (_, { dispatch, getState }) => {
  if (!wsSubscribed) {
    wsSubscribed = true;
    chatSocket.onStatusChange((s) => {
      dispatch(setWsConnected(s === 'connected'));
    });
    chatSocket.onMessage((type, data) => {
      const state = getState() as RootState;
      switch (type) {
        case 'message': {
          const msg = data as Message;
          dispatch(upsertMessage(msg));
          const active = state.chat.activeConversationId;
          if (active === msg.conversation_id) {
            dispatch(clearUnread(msg.conversation_id));
            api.markRead(msg.conversation_id).catch(() => {});
          } else {
            dispatch(incrementUnread(msg.conversation_id));
          }
          break;
        }
        case 'message_update':
          dispatch(applyMessageUpdate(data as Message));
          break;
        case 'send_error':
          dispatch(markSendError(data));
          break;
      }
    });
  }
  chatSocket.connect();
});

// 退出聊天页时关闭连接(停止自动重连)
export const closeChatSocket = createAsyncThunk('chat/closeSocket', async () => {
  chatSocket.close();
});

export const refreshConversations = createAsyncThunk('chat/refreshConversations', async () => {
  return await api.conversations();
});

export const refreshContacts = createAsyncThunk('chat/refreshContacts', async () => {
  return await api.contacts();
});

export const refreshBlocked = createAsyncThunk('chat/refreshBlocked', async () => {
  return await api.blockedUsers();
});

export const refreshPendingRequests = createAsyncThunk('chat/refreshPendingRequests', async () => {
  return await api.friendRequests();
});

export const loadMessages = createAsyncThunk('chat/loadMessages', async (cid: number, { getState }) => {
  const state = getState() as RootState;
  if (state.chat.loaded[cid]) return null;
  const msgs = await api.messages(cid, 0, 50);
  return { cid, msgs };
});

export const loadMoreMessages = createAsyncThunk('chat/loadMoreMessages', async (cid: number, { getState }) => {
  const state = getState() as RootState;
  const list = state.chat.messages[cid] || [];
  const oldest = list.length ? list[0].id : 0;
  const msgs = await api.messages(cid, oldest, 50);
  return { cid, msgs };
});

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async (payload: { cid: number; content: string; replyToId?: number }, { getState }) => {
    const state = getState() as RootState;
    const me = state.user.user;
    const clientId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const optimistic: Message = {
      id: -Date.now(),
      conversation_id: payload.cid,
      sender_id: me?.id ?? 0,
      sender: me ?? undefined,
      content: payload.content,
      type: 'text',
      client_id: clientId,
      reply_to_id: payload.replyToId || 0,
      created_at: new Date().toISOString(),
      status: 'sending',
    };
    const ok = chatSocket.send('message', {
      conversation_id: payload.cid,
      content: payload.content,
      client_id: clientId,
      reply_to_id: payload.replyToId || 0,
    });
    return { optimistic, ok };
  },
);

export const retryMessage = createAsyncThunk(
  'chat/retryMessage',
  async (payload: { cid: number; clientId: string; content: string }, { getState }) => {
    const state = getState() as RootState;
    const me = state.user.user;
    const newClientId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const optimistic: Message = {
      id: -Date.now(),
      conversation_id: payload.cid,
      sender_id: me?.id ?? 0,
      sender: me ?? undefined,
      content: payload.content,
      type: 'text',
      client_id: newClientId,
      created_at: new Date().toISOString(),
      status: 'sending',
    };
    const ok = chatSocket.send('message', {
      conversation_id: payload.cid,
      content: payload.content,
      client_id: newClientId,
    });
    return { oldClientId: payload.clientId, optimistic, ok };
  },
);

// ---------- slice ----------

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setView: (state, action: PayloadAction<View>) => {
      state.view = action.payload;
      // 右侧详情区跟随视图切换:
      // - 会话视图 → 聊天内容
      // - 通讯录视图 → 个人名片(保留已有选中,否则默认第一人)
      // - 群聊视图 → 群聊详情(保留已有选中,否则默认第一个群)
      if (action.payload === 'conversations') {
        state.rightContent = { type: 'chat' };
      } else if (action.payload === 'contacts') {
        const already = state.rightContent;
        const isContactsContent =
          already.type === 'contact-card' ||
          (already.type === 'panel' &&
            (already.panel === 'add-friend' || already.panel === 'requests' || already.panel === 'blocked'));
        if (!isContactsContent) {
          state.rightContent = state.contacts.length
            ? { type: 'contact-card', userId: state.contacts[0].id }
            : { type: 'none' };
        }
      } else {
        const already = state.rightContent;
        const isGroupsContent =
          already.type === 'group-detail' || (already.type === 'panel' && already.panel === 'new-group');
        if (!isGroupsContent) {
          const groups = state.conversations.filter((c) => c.type === 'group');
          state.rightContent = groups.length ? { type: 'group-detail', groupId: groups[0].id } : { type: 'none' };
        }
      }
    },
    setRightContent: (state, action: PayloadAction<RightContent>) => {
      state.rightContent = action.payload;
    },
    setActiveConversation: (state, action: PayloadAction<number | null>) => {
      state.activeConversationId = action.payload;
    },
    setWsConnected: (state, action: PayloadAction<boolean>) => {
      state.wsConnected = action.payload;
    },
    upsertMessage: (state, action: PayloadAction<Message>) => {
      const msg = action.payload;
      const list = state.messages[msg.conversation_id] || [];
      const idx = list.findIndex((m) => m.client_id && m.client_id === msg.client_id && m.id < 0);
      if (idx >= 0) {
        list[idx] = { ...msg, status: 'sent' };
      } else if (!list.some((m) => m.id === msg.id)) {
        list.push(msg);
      }
      state.messages[msg.conversation_id] = sortMessages(state.messages[msg.conversation_id]);
      const conv = state.conversations.find((c) => c.id === msg.conversation_id);
      if (conv) {
        conv.last_message = msg;
        state.conversations = sortConversations(state.conversations);
      }
    },
    applyMessageUpdate: (state, action: PayloadAction<Message>) => {
      const msg = action.payload;
      const list = state.messages[msg.conversation_id] || [];
      const idx = list.findIndex((m) => m.id === msg.id);
      if (idx >= 0) {
        list[idx] = msg;
        state.messages[msg.conversation_id] = [...list];
      }
      const conv = state.conversations.find((c) => c.id === msg.conversation_id);
      if (conv && conv.last_message?.id === msg.id) {
        conv.last_message = msg;
      }
    },
    markSendError: (state, action: PayloadAction<{ clientId: string; reason: string }>) => {
      for (const cid of Object.keys(state.messages)) {
        const list = state.messages[Number(cid)];
        const idx = list.findIndex((m) => m.client_id === action.payload.clientId);
        if (idx >= 0) {
          list[idx].status = 'failed';
          list[idx].sendError = action.payload.reason;
          state.messages[Number(cid)] = [...list];
          break;
        }
      }
    },
    incrementUnread: (state, action: PayloadAction<number>) => {
      const conv = state.conversations.find((c) => c.id === action.payload);
      if (conv) conv.unread += 1;
    },
    clearUnread: (state, action: PayloadAction<number>) => {
      const conv = state.conversations.find((c) => c.id === action.payload);
      if (conv) conv.unread = 0;
    },
    removeConversation: (state, action: PayloadAction<number>) => {
      state.conversations = state.conversations.filter((c) => c.id !== action.payload);
      if (state.activeConversationId === action.payload) {
        state.activeConversationId = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initChat.fulfilled, (state, action) => {
        state.conversations = sortConversations(action.payload.conversations);
        state.contacts = action.payload.contacts;
        state.blocked = action.payload.blocked;
        state.pendingRequests = action.payload.pendingRequests;
        state.initialized = true;
      })
      .addCase(refreshConversations.fulfilled, (state, action) => {
        state.conversations = sortConversations(action.payload);
      })
      .addCase(refreshContacts.fulfilled, (state, action) => {
        state.contacts = action.payload;
      })
      .addCase(refreshBlocked.fulfilled, (state, action) => {
        state.blocked = action.payload;
      })
      .addCase(refreshPendingRequests.fulfilled, (state, action) => {
        state.pendingRequests = action.payload;
      })
      .addCase(loadMessages.fulfilled, (state, action) => {
        if (!action.payload) return;
        const { cid, msgs } = action.payload;
        // 后端 ListMessagesForUser 已返回升序(倒序分页后翻转正序),直接使用
        state.messages[cid] = msgs;
        state.loaded[cid] = true;
        state.hasMore[cid] = msgs.length >= 50;
      })
      .addCase(loadMoreMessages.fulfilled, (state, action) => {
        const { cid, msgs } = action.payload;
        if (!msgs.length) {
          state.hasMore[cid] = false;
          return;
        }
        const current = state.messages[cid] || [];
        const existingIds = new Set(current.map((m) => m.id));
        // 后端返回升序(旧→新),过滤已有后整体前置
        const older = msgs.filter((m) => !existingIds.has(m.id));
        state.messages[cid] = [...older, ...current];
        state.hasMore[cid] = msgs.length >= 50;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        const { optimistic, ok } = action.payload;
        if (!ok) {
          optimistic.status = 'failed';
          optimistic.sendError = '连接已断开,请检查网络后重试';
        }
        const list = state.messages[optimistic.conversation_id] || [];
        state.messages[optimistic.conversation_id] = sortMessages([...list, optimistic]);
        const conv = state.conversations.find((c) => c.id === optimistic.conversation_id);
        if (conv) {
          conv.last_message = optimistic;
          state.conversations = sortConversations(state.conversations);
        }
      })
      .addCase(retryMessage.fulfilled, (state, action) => {
        const { oldClientId, optimistic, ok } = action.payload;
        if (!ok) {
          optimistic.status = 'failed';
          optimistic.sendError = '连接已断开,请检查网络后重试';
        }
        const list = state.messages[optimistic.conversation_id] || [];
        const failedIdx = list.findIndex((m) => m.client_id === oldClientId);
        const result = [...list];
        if (failedIdx >= 0) result[failedIdx] = optimistic;
        else result.push(optimistic);
        state.messages[optimistic.conversation_id] = sortMessages(result);
        const conv = state.conversations.find((c) => c.id === optimistic.conversation_id);
        if (conv) {
          conv.last_message = optimistic;
          state.conversations = sortConversations(state.conversations);
        }
      });
  },
});

export const {
  setView,
  setRightContent,
  setActiveConversation,
  setWsConnected,
  upsertMessage,
  applyMessageUpdate,
  markSendError,
  incrementUnread,
  clearUnread,
  removeConversation,
} = chatSlice.actions;
export default chatSlice.reducer;

